import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { upload_homepage_hero_image } from "../../../reduxData/cms/cmsAction";
import { resolveImageUrl } from "./homepageHero.helpers";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

function getImageExtension(fileName = "") {
  return fileName.toLowerCase().match(/\.[^.]+$/)?.[0] || "";
}

function getOutputType(file) {
  if (file.type === "image/jpg") return "image/jpeg";
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return file.type;

  const extension = getImageExtension(file.name);
  if (extension === ".png") return "image/png";
  if (extension === ".webp") return "image/webp";
  return "image/jpeg";
}

function createImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = source;
  });
}

async function createCroppedFile(source, cropArea, originalFile, outputWidth, outputHeight) {
  const image = await createImage(source);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const outputType = getOutputType(originalFile);

  canvas.width = outputWidth;
  canvas.height = outputHeight;
  context.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => result ? resolve(result) : reject(new Error("Unable to crop image")),
      outputType,
      0.9,
    );
  });

  return new File([blob], originalFile.name, { type: outputType });
}

function ImageCropModal({ file, aspect, outputWidth, outputHeight, outputLabel, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState(null);
  const [processing, setProcessing] = useState(false);
  const imageUrl = useRef(URL.createObjectURL(file));
  const outputSize = { width: outputWidth, height: outputHeight, label: outputLabel };

  useEffect(() => () => URL.revokeObjectURL(imageUrl.current), []);

  const handleConfirm = async () => {
    if (!cropArea) return;
    setProcessing(true);
    try {
      const croppedFile = await createCroppedFile(
        imageUrl.current,
        cropArea,
        file,
        outputSize.width,
        outputSize.height,
      );
      await onConfirm(croppedFile);
    } finally {
      setProcessing(false);
    }
  };

  const changeZoom = (amount) => {
    setZoom((current) => Math.min(3, Math.max(1, Number((current + amount).toFixed(2)))));
  };

  return (
    <Modal show centered onHide={onCancel} className="hero-crop-modal" dialogClassName="hero-crop-dialog" backdrop="static" keyboard={!processing}>
      <Modal.Header closeButton>
        <div className="hero-modal-heading">
          <span className="hero-modal-heading__icon">⌗</span>
          <div>
          <Modal.Title>Crop image</Modal.Title>
          <p>Drag the image to reposition it inside the fixed crop frame.</p>
          </div>
          <span className="hero-crop-size">{outputSize.label}</span>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="hero-crop-workspace">
          <Cropper
            image={imageUrl.current}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setCropArea(pixels)}
          />
        </div>

        <div className="hero-zoom-control">
          <span>Zoom</span>
          <button type="button" onClick={() => changeZoom(-0.1)} aria-label="Zoom out">−</button>
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
          />
          <button type="button" onClick={() => changeZoom(0.1)} aria-label="Zoom in">+</button>
          <strong>{Math.round(zoom * 100)}%</strong>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="hero-modal-button hero-modal-button--neutral" disabled={processing} onClick={onCancel}>Cancel</button>
        <button type="button" className="hero-modal-button hero-modal-button--primary" disabled={processing || !cropArea} onClick={handleConfirm}>
          {processing ? "Preparing…" : "Crop & Upload"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default function HeroImageUploader({
  device,
  label,
  value,
  onChange,
  aspect,
  dimensions,
  outputWidth,
  outputHeight,
  disabled,
  fieldError,
  dispatch,
}) {
  const inputRef = useRef(null);
  const [sourceFile, setSourceFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [dragging, setDragging] = useState(false);

  const selectFile = useCallback((file) => {
    setUploadError("");
    if (!file) return;
    const hasAcceptedType = ACCEPTED_IMAGE_TYPES.includes(file.type);
    const hasAcceptedExtension = ACCEPTED_IMAGE_EXTENSIONS.includes(getImageExtension(file.name));
    if (!hasAcceptedType && !hasAcceptedExtension) {
      setUploadError("Choose a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setUploadError("The selected image is larger than 5 MB.");
      return;
    }
    setSourceFile(file);
  }, []);

  const uploadCroppedImage = async (croppedFile) => {
    setUploading(true);
    setUploadError("");
    try {
      if (!(croppedFile instanceof File) || croppedFile.size === 0) {
        throw new Error("The cropped image is empty.");
      }

      const response = await upload_homepage_hero_image(croppedFile, dispatch);
      const uploadedPath = response?.data?.data?.[0]
        ?? response?.data?.[0]
        ?? response?.data?.path
        ?? null;

      if (typeof uploadedPath !== "string" || !uploadedPath.trim()) {
        throw new Error("The upload response did not contain an image path.");
      }

      onChange(uploadedPath);
      setSourceFile(null);
    } catch (error) {
      setUploadError("Upload failed. Your previous image was kept.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) selectFile(event.dataTransfer.files?.[0]);
  };

  return (
    <div className={`hero-image-card hero-image-card--${device || "desktop"}`} style={{ "--hero-image-aspect": aspect }}>
      <div className="hero-image-card__heading">
        <div>
          <strong>{label}</strong>
          <span>Recommended {dimensions}</span>
        </div>
        <span className="hero-image-status">{value ? "Ready" : "Required"}</span>
      </div>

      {value ? (
        <div className="hero-image-preview">
          <img src={resolveImageUrl(value)} alt={`${label} preview`} />
          {uploading && <div className="hero-image-loading"><Spinner animation="border" size="sm" /> Uploading…</div>}
        </div>
      ) : (
        <button
          type="button"
          className={`hero-dropzone ${dragging ? "is-dragging" : ""}`}
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <span className="hero-dropzone__icon">▧</span>
          <strong>Upload {label.toLowerCase()}</strong>
          <span>Drag and drop or choose an image</span>
          <small>{dimensions} · JPG, PNG or WEBP · Max 5 MB</small>
        </button>
      )}

      <input
        ref={inputRef}
        hidden
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        onChange={(event) => {
          selectFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />

      {value && !uploading && (
        <div className="hero-image-card__footer">
          <div className="hero-image-file-info" title={value}>
            <strong>{String(value).split("/").pop()}</strong>
            <span>{dimensions}</span>
          </div>
          <div className="hero-image-actions">
            <button type="button" className="hero-link-button" disabled={disabled} onClick={() => inputRef.current?.click()}>
              Replace
            </button>
            <button type="button" className="hero-link-button hero-link-button--danger" disabled={disabled} onClick={() => onChange("")}>
              Remove
            </button>
          </div>
        </div>
      )}

      {fieldError && <div className="hero-field-error">{fieldError}</div>}
      {uploadError && <div className="hero-upload-error">{uploadError}</div>}

      {sourceFile && (
        <ImageCropModal
          file={sourceFile}
          aspect={aspect}
          outputWidth={outputWidth}
          outputHeight={outputHeight}
          outputLabel={`${label} · ${dimensions}`}
          onCancel={() => setSourceFile(null)}
          onConfirm={uploadCroppedImage}
        />
      )}
    </div>
  );
}
